import React, {useCallback, useEffect, useRef, useState} from "react";
import ConsentController from "@source/api/controllers/consentController";
import ReqError from "@source/api/reqError";
import Button from "@source/components/UI/buttons/button/button";
import {getConsentPreviewErrorDescription} from "@source/api/models/consentHelpers";
import {ConsentDocumentPreview as ConsentDocumentPreviewResponse} from "@source/api/models/consentModels";
import styles from "./consentDocumentPreview.module.scss";
import {authorizedPreviewSrcDoc} from "./consentDocumentPreviewSource";
import {ScaledDocumentFrame} from "./scaledDocumentFrame";

type PreviewState = ConsentDocumentPreviewResponse & {url?: string; srcDoc?: string};

const previewErrorDescription = (error: unknown) => {
  const status = error instanceof ReqError ? error.status : undefined;
  const knownError = getConsentPreviewErrorDescription(status);
  if (knownError) return knownError;
  return error instanceof Error ? error.message : "Unable to load the form preview.";
};

export interface ConsentDocumentPreviewProps {
  documentId: string;
  autoLoad?: boolean;
  loadLabel?: string;
  className?: string;
  /** Fill the height given by `className` instead of using the default minimum height. */
  fill?: boolean;
  /** Reports whether a supported, authorized preview is currently rendered. */
  onReadinessChange?: (ready: boolean) => void;
}

export const ConsentDocumentPreview = ({
  documentId,
  autoLoad = true,
  loadLabel = "Load preview",
  className = "",
  fill = false,
  onReadinessChange,
}: ConsentDocumentPreviewProps) => {
  const [preview, setPreview] = useState<PreviewState>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const mountedRef = useRef(true);
  const requestIdRef = useRef(0);
  const previewUrlRef = useRef<string>();

  const reportReadiness = useCallback((ready: boolean) => {
    onReadinessChange?.(ready);
  }, [onReadinessChange]);

  const revokePreviewUrl = () => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = undefined;
    }
  };

  useEffect(() => () => {
    mountedRef.current = false;
    requestIdRef.current += 1;
    reportReadiness(false);
    revokePreviewUrl();
  }, [reportReadiness]);

  useEffect(() => {
    mountedRef.current = true;
  }, []);

  const load = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError("");
    reportReadiness(false);
    try {
      const response = await ConsentController.getAuthorizedPreview(documentId);
      if (response.contentType !== "text/html; charset=utf-8" && response.contentType !== "application/pdf") {
        throw new Error("The form preview returned an unsupported content type.");
      }
      const isHtml = response.contentType === "text/html; charset=utf-8";
      const url = isHtml ? undefined : URL.createObjectURL(new Blob([response.bytes], {type: response.contentType}));
      if (!mountedRef.current || requestId !== requestIdRef.current) {
        if (url) URL.revokeObjectURL(url);
        return;
      }
      revokePreviewUrl();
      previewUrlRef.current = url;
      setPreview({...response, ...(isHtml ? {srcDoc: authorizedPreviewSrcDoc(response.bytes)} : {url})});
    } catch (requestError: unknown) {
      if (mountedRef.current && requestId === requestIdRef.current) {
        setPreview(undefined);
        setError(previewErrorDescription(requestError));
        reportReadiness(false);
      }
    } finally {
      if (mountedRef.current && requestId === requestIdRef.current) setLoading(false);
    }
  }, [documentId, reportReadiness]);

  useEffect(() => {
    requestIdRef.current += 1;
    revokePreviewUrl();
    setPreview(undefined);
    setError("");
    reportReadiness(false);
    if (autoLoad) void load();
  }, [autoLoad, documentId, load, reportReadiness]);

  const openPreview = () => {
    if (preview?.url) window.open(preview.url, "_blank", "noopener,noreferrer");
  };

  if (loading) return <div className={`${styles.state} ${className}`} role="status">Loading authorized form preview…</div>;
  if (error) return <div className={`${styles.state} ${className}`} role="alert"><p>{error}</p><Button size="small" variant="outlined" onClick={() => void load()}>Retry preview</Button></div>;
  if (!preview) return <Button className={className} size="small" variant="outlined" onClick={() => void load()}>{loadLabel}</Button>;

  if (preview.contentType === "text/html; charset=utf-8") {
    return <div className={`${styles.preview} ${fill ? styles.fill : ""} ${className}`}>
      <ScaledDocumentFrame srcDoc={preview.srcDoc} title="Authorized form preview" onLoad={() => reportReadiness(true)}/>
    </div>;
  }

  return <div className={`${styles.preview} ${fill ? styles.fill : ""} ${className}`}>
    <iframe className={styles.frame} src={preview.url} title="Authorized form PDF preview" onLoad={() => reportReadiness(true)}/>
    <Button size="small" variant="outlined" onClick={openPreview}>Open preview in new tab</Button>
  </div>;
};
